import dedent from 'ts-dedent'
import { MILES_TO_M } from '../const'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumn } from '../utils/schemas'
import { SelectWorkflowComponent } from './common/selectcomponent'

class Area extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.area',
        group: 'Spatial Operations',
        title: 'ST Area',
        description: `
      This component creates a new table with an additional column containing the area
      of the geographies in the input table. This only applies to polygons.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source polygons table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'units',
          type: WorkflowComponentParamType.Selection,
          title: 'Units',
          options: ['Meters', 'Miles'],
          default: 'Meters'
        }
      ]
    )
  }

  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Postgres,
      Provider.Redshift,
      Provider.Snowflake,
      Provider.DatabricksRest
    ]
  }

  getInnerSql({ params, connection }: WorkflowComponentCodeParams) {
    const convert =
      params.units === 'Meters' ? '' : ` / (${MILES_TO_M}*${MILES_TO_M})`
    switch (connection.type) {
      case Provider.Redshift:
      case Provider.Postgres:
        // converted to geography that always give back sqm to avoid to
        // evaluate CRS
        return dedent`SELECT *,
                  (
                    ST_AREA(${params.geo}::geography)
                  ) ${convert}
                  AS
                    ${params.geo}_area
                FROM
                  ${connection.quoteName(params.source as string)}`
      case Provider.Snowflake:
        // converted to geography that always give back sqm to avoid to
        // evaluate CRS
        return dedent`SELECT *,
                  (
                    ST_AREA(TO_GEOGRAPHY(${params.geo}))
                  ) ${convert}
                  AS
                    ${params.geo}_area
                FROM
                  ${connection.quoteName(params.source as string)}`
      case Provider.BigQuery:
        return dedent`SELECT *,
                  (
                    ST_AREA(${params.geo})
                  ) ${convert}
                  AS
                   ${params.geo}_area
                FROM
                  ${connection.quoteName(params.source as string)}`
      case Provider.DatabricksRest:
        return dedent`SELECT /*+ REPARTITION(40) */
                  *,
                  (
                    ST_AREA(${connection.processInputGeogColumn(
                      params.geo as string
                    )})
                  ) ${convert}
                  AS
                    ${params.geo}_area
                FROM
                  ${connection.quoteName(params.source as string)}`
      default:
        return null
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    return addColumn(columns.source, {
      name: `${params.geo}_area`,
      type: ColumnDataType.Number
    })
  }
}

export { Area }
